/**
 * Sprite cache for the canvas renderer: one design = one SVG rasterized ONCE
 * into an offscreen canvas, then a single drawImage per car per frame — the
 * canvas equivalent of "one SVG moved with a transform". Rasterized at 6× the
 * world size so it stays crisp at every real zoom×dpr; the roof number's font
 * is embedded in the SVG (an <img> decode cannot fetch external fonts).
 *
 * While a sprite is decoding (or in a headless environment), get() returns
 * null and the renderer draws its flat fallback silhouette for a frame or two.
 */

import type { CarConfig } from '@microligue/shared';
import { CAR_LENGTH_PX, CAR_VIEW } from '@microligue/shared';
import { BUNGEE_DATA_URI } from './font';
import { carSvgString } from './svg';

/** Sprite pixels per world px. */
const SUPERSAMPLE = 6;
/** Designs kept before a full reset (leavers/joiners churn) — ~68 KB each. */
const MAX_ENTRIES = 64;

function spriteKey(c: CarConfig): string {
  return [
    c.body,
    c.rubber,
    c.stripe,
    c.stripeStyle,
    c.glass,
    c.spoiler ? 1 : 0,
    c.spoilerColor,
    c.decal,
    c.number,
    c.flag,
    c.decalColor,
  ].join('|');
}

export class CarSpriteCache {
  /** null = decoding (or unavailable): the caller falls back for now. */
  private readonly entries = new Map<string, HTMLCanvasElement | null>();

  get(cfg: CarConfig): HTMLCanvasElement | null {
    const key = spriteKey(cfg);
    const hit = this.entries.get(key);
    if (hit !== undefined) return hit;
    if (this.entries.size > MAX_ENTRIES) this.entries.clear();
    this.entries.set(key, null);
    try {
      // Headless (tests) or exotic browsers: stay on the fallback silhouette.
      if (typeof Image === 'undefined' || typeof document === 'undefined') return null;
      const svg = carSvgString(cfg, { shadow: true, fontDataUri: BUNGEE_DATA_URI });
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        canvas.height = CAR_LENGTH_PX * SUPERSAMPLE;
        canvas.width = Math.round((CAR_VIEW.w / CAR_VIEW.h) * canvas.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        this.entries.set(key, canvas);
      };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    } catch {
      // Blob/URL unavailable: permanent fallback for this design.
    }
    return null;
  }
}
